import React, { useState, useEffect, useRef } from 'react';
import './PlayOffDownDetails.css';

import AnimateHeight from 'react-animate-height';

import axios from "axios";
import Partie from '../Partie';
import { URL_WS_URL, URL_WS_PORT, domain } from '../../variables';
import CartridgePlayers from '../CartridgePlayers';
import io from 'socket.io-client';

function getWindowSize() {
    const {innerWidth, innerHeight} = window;
    return {innerWidth, innerHeight};
}

// Rapproche un nom de joueur entre getRencDetails.php ("NOM Prenom", casse
// normale) et le livescoring dafunker (parfois "NOM PRENOM" en majuscules,
// avec ou sans accents selon les matchs) : on compare sans tenir compte de la
// casse ni des accents pour ne pas rater un rapprochement valide.
function normalizePlayerName(name) {
    return (name || "")
        .toUpperCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");
}

const PlayOffDownDetails = ({
    rencId,
    homeClub,
    awayClub,
    deploy,
    headerLivescore,
    division,
    match,
    showDetails
}) => {
    const [socket, setSocket] = useState(null);
    const [details, setDetails] = useState([]);

    const [livescore, setLivescore] = useState([]);
    const [jsonMatch, setJsonMatch] = useState(null);

    const livescoreRef = useRef(livescore);
    const jsonMatchRef = useRef(jsonMatch);

    const [isOnLive, setIsOnLive] = useState(false);
    const [liveTeamA, setLiveTeamA] = useState([]);
    const [liveTeamX, setLiveTeamX] = useState([]);
    // Drapeaux du cartouche (mode "SPID", résultats officiels) : non fournis
    // par getRencDetails.php, complétés via le livescoring (cf. refreshHistoriqueForDetails).
    const [liveTeamACountryCodes, setLiveTeamACountryCodes] = useState([]);
    const [liveTeamXCountryCodes, setLiveTeamXCountryCodes] = useState([]);
    // Noms bruts (sans le "N°.. - classement") des joueurs du cartouche, dans
    // l'ordre A/B/C et X/Y/Z de getRencDetails.php. Cet ordre ne correspond pas
    // forcément à l'ordre des slots JA1..JA4/JB1..JB4 côté dafunker (constaté
    // sur un match où X/Y étaient inversés) : on rapproche donc par nom exact
    // plutôt que par position dans refreshHistoriqueForDetails.
    const teamANamesRef = useRef([]);
    const teamXNamesRef = useRef([]);

    useEffect(() => {
        if (deploy === rencId) {
            if (headerLivescore.status !== "en_cours") {
                getDetailsMatch();
            }
        }

        if (details.length === 0) {
            getDetailsMatch();
            // RAJOUTER UNE CONDITION POUR SAVOIR SI IL Y A DES DETAILS
            // SI CEST LE CAS ALORS ON NE RECUPERE PAS LES MATCHS DU LIVESCORING
            if (headerLivescore.status === "termine") {
                getDetailsDafunker();
            }
        }

        // on ne veut pas que la connexion socket soit établit si le match est fermé
        if (!showDetails) {
            // si livescoring
            if (headerLivescore.status === 'en_cours' && socket !== null) {
                // on désactive le socket si on n'a pas déroulé le match
                socket.disconnect();
                setSocket(null);
                setIsOnLive(false);
                return;
            }
        }

        // on initialise le socket pour les matchs déroulés d'aujourd'hui 
        if (headerLivescore.status === 'en_cours') {
            const link = URL_WS_URL + ":" + URL_WS_PORT + "/livescore_" + rencId + "?" + getCData();
            const tmpSocket = io(link);

            // Message de connexion
            tmpSocket.on('connect', () => {
            });
    

            // Ecouter les messages d'update de l'entete du match
            tmpSocket.on('update', (jsonSocket) => {
                setIsOnLive(true);
                parseLivescoreForPartie(jsonSocket);
                jsonMatchRef.current = jsonSocket;
                setJsonMatch(jsonSocket);
            });

            // Ecouter les messages d'update du match
            tmpSocket.on('update_partie', (jsonPartie) => {
                setIsOnLive(true);
                mergeJsonPartieWithLivescore(jsonPartie);
            });

    
            // on sauvegarde le socket
            setSocket(tmpSocket);
    
            // Nettoyage de l'effet lors du démontage du composant
            return () => {
                setIsOnLive(false);
                tmpSocket.disconnect();
            };
        }

    }, [showDetails])

    const getDetailsMatch = async () => {
        try {
            const url = `${domain}/server/getRencDetails.php?renc_id=${rencId}`;
            axios.get(url)
            .then((res) => {
                const data = res.data;

                // on vérifie que le match ait bien des parties jouées
                if (data.parties && data.parties.length > 0) {
                    setDetails(parseJSONDetails(data));
                    // Le match est terminé (résultats officiels) : on interroge en plus
                    // le livescoring (dafunker) pour récupérer l'historique détaillé
                    // (score_history) de chaque partie et permettre l'affichage
                    // de "Historique" même sur un match terminé.
                    refreshHistoriqueForDetails();
                }
            })
        }
        catch (error) {
            console.log(error);
        }
    }

    // Complète les parties officielles (getRencDetails.php, sans score_history)
    // avec l'historique détaillé récupéré depuis le livescoring (dafunker), en
    // faisant correspondre chaque partie par nom des deux joueurs (les deux
    // sources listent les parties dans le même ordre mais dafunker est la
    // seule à exposer un identifiant par partie).
    const refreshHistoriqueForDetails = async () => {
        try {
            const link = URL_WS_URL + "/livescore/get/" + rencId;
            const res = await axios.get(link, {
                "Content-Type": "application/xml; charset=utf-8"
            });
            const dafunkerMatch = res.data;
            if (!dafunkerMatch?.parties || !dafunkerMatch?.equipe1 || !dafunkerMatch?.equipe2) {
                return;
            }

            const getFullName = (equipe, slotId) => {
                const joueur = equipe?.joueurs?.[slotId];
                return joueur ? normalizePlayerName(joueur.nom + " " + joueur.prenom) : "";
            }

            setDetails(prevDetails => prevDetails.map((partie) => {
                const dafunkerPartie = dafunkerMatch.parties.find((p) =>
                    getFullName(dafunkerMatch.equipe1, p.joueur1.slotId) === normalizePlayerName(partie.ja) &&
                    getFullName(dafunkerMatch.equipe2, p.joueur2.slotId) === normalizePlayerName(partie.jb)
                );
                if (!dafunkerPartie) {
                    return partie;
                }
                return {
                    ...partie,
                    score_history: dafunkerPartie.score_history,
                    premier_serveur: dafunkerPartie.premier_serveur,
                    cards: dafunkerPartie.cards,
                    countryCodeJA: dafunkerMatch.equipe1?.joueurs?.[dafunkerPartie.joueur1.slotId]?.country_code,
                    countryCodeJB: dafunkerMatch.equipe2?.joueurs?.[dafunkerPartie.joueur2.slotId]?.country_code,
                };
            }));

            // Idem pour le cartouche des joueurs (mode "SPID"). L'ordre A/B/C
            // (resp. X/Y/Z) de getRencDetails.php ne correspond pas toujours à
            // l'ordre des slots JA1..JA4 (resp. JB1..JB4) côté dafunker : on
            // rapproche donc chaque joueur par nom (normalisé), tous slots
            // confondus, plutôt que par position.
            const countryCodeByName = {};
            [dafunkerMatch.equipe1, dafunkerMatch.equipe2].forEach((equipe) => {
                Object.values(equipe?.joueurs || {}).forEach((joueur) => {
                    if (!joueur) return;
                    countryCodeByName[normalizePlayerName(joueur.nom + " " + joueur.prenom)] = joueur.country_code;
                });
            });
            setLiveTeamACountryCodes(teamANamesRef.current.map(nom => countryCodeByName[normalizePlayerName(nom)]));
            setLiveTeamXCountryCodes(teamXNamesRef.current.map(nom => countryCodeByName[normalizePlayerName(nom)]));
        }
        catch (error) {
            console.log(error);
        }
    }

    const getDetailsDafunker = async () => {
        const link = URL_WS_URL + "/livescore/get/" + rencId;
        axios
        .get(link, {
            "Content-Type": "application/xml; charset=utf-8"
        })
        .then(function(res) {
            const data = res.data;
            parseLivescoreForPartie(data);
        })
        .catch(function(error) {
            console.log(error);
        });
    }

    const getCData = () => {
        const link = "renc_id=" + rencId
        + "&is_retour=0" 
        + "&phase=2" 
        + "&res_1=0" 
        + "&res_2=0" 
        + "&equip_1=" + match.domicile.replaceAll(" ","+") 
        + "&equip_2=" +match.exterieur.replaceAll(" ","+")
        + "&equip_id1=00000"
        + "&equip_id2=00000" 
        + "&clubnum_1=" + match.clubnum_1  
        + "&clubnum_2=" + match.clubnum_2;
        return link;
    }

    const parseJSONDetails = (data) => {
        const parties = [];
        const teamA = [];
        const teamX = [];

        (data.parties || []).forEach((partie) => {
            const object = {};
            Object.keys(partie).forEach((key) => {
                // on adapte le score pour correspondre au format livescore
                // 09 -05 07 -09 06 ->>>> [[11, 9], [5, 11], [11, 7], [9, 11], [11, 6]]
                if (key === "detail") {
                    // notre array qui contient le score
                    const sets = [];
                    // on split le string pour récupérer chaque set
                    const scores = partie.detail.split(' ');
                    // on insère chaque set dans un array pour correspondre au bon format
                    scores.forEach((score) => {
                        const scoreBrut = parseInt(score.replace('-', ''));
                        let scoreJA = 0; let scoreJB = 0;

                        // défaite du joueur A
                        if (score.includes('-')) {
                            scoreJA = parseInt(scoreBrut);
                            scoreJB = parseInt(scoreBrut >= 10 ? scoreBrut + 2 : 11);
                        }
                        // victoire du joueur A
                        else {
                            scoreJA = parseInt(scoreBrut >= 10 ? scoreBrut + 2 : 11);
                            scoreJB = parseInt(scoreBrut);
                        }

                        // à noter que le parseInt retire le 0 devant le score
                        // utile pour récupérer le score directement

                        // on insère les scores dans notre array
                        sets.push([scoreJA, scoreJB]);
                    });
                    object.detail = sets;
                }
                else {
                    object[key] = partie[key];
                }
            })
            // on insère la partie
            parties.push(object);
        });

        const teamANames = [];
        const teamXNames = [];

        (data.joueurs || []).forEach((joueur) => {
            teamA.push(joueur[0] + " " + joueur[1]);
            teamANames.push(joueur[0]);
            teamX.push(joueur[2] + " " + joueur[3]);
            teamXNames.push(joueur[2]);
        })
        setLiveTeamA(teamA);
        teamANamesRef.current = teamANames;
        setLiveTeamX(teamX);
        teamXNamesRef.current = teamXNames;
        return parties;
    }

    const CalSetA = (score) =>  {
        let total = 0;
        score.forEach((game) => {
           
            if(game[0] > game[1])
            {
                total = total + 1;
            }
        })
        return total;
    }

    const CalSetX = (score) =>  {
        let total = 0;
        score.forEach((game) => {
           
            if(game[0] < game[1])
            {
                total = total + 1;
            }
        })
        return total;
    }

    const parseLivescoreForPartie = (match) => {
        const parties = [];
        match.parties.forEach((partie) => {

             // on récupère le slotID des joueurs
            const slotIdJA = partie.joueur1.slotId;
            const slotIdJB = partie.joueur2.slotId;
            let fullNameJA = ""; let fullNameJB = "";

            if (slotIdJA !== "") {
                fullNameJA = match.equipe1.joueurs[slotIdJA].nom + " " + match.equipe1.joueurs[slotIdJA].prenom;
            }

            if (slotIdJB !== "") {
                fullNameJB = match.equipe2.joueurs[slotIdJB].nom + " " + match.equipe2.joueurs[slotIdJB].prenom;
            }
            
            // Ne pas prendre en compte les matchs sans joueurs définis pour l'afficher
            if(fullNameJA !== "" && fullNameJB !== "")
            {
                parties.push({
                    "id": partie.id,
                    "ja": fullNameJA,
                    "jb": fullNameJB,
                    "scorea": '' + CalSetA(partie.score),
                    "scoreb": '' + CalSetX(partie.score),
                    "detail": partie.score,
                    "premier_serveur": partie.premier_serveur,
                    "score_history": partie.score_history,
                    "cards": partie.cards,
                    "statut": partie.status,
                    "countryCodeJA": match.equipe1?.joueurs?.[slotIdJA]?.country_code,
                    "countryCodeJB": match.equipe2?.joueurs?.[slotIdJB]?.country_code,
                });
            }   
        })
        livescoreRef.current = parties;
        setLivescore(parties);

        const joueursA = [];
        joueursA.push(match?.equipe1?.joueurs?.JA1);
        joueursA.push(match?.equipe1?.joueurs?.JA2);
        joueursA.push(match?.equipe1?.joueurs?.JA3);
        joueursA.push(match?.equipe1?.joueurs?.JA4);
        setLiveTeamA(joueursA);

        const joueursX = [];
        joueursX.push(match?.equipe2.joueurs?.JB1);
        joueursX.push(match?.equipe2.joueurs?.JB2);
        joueursX.push(match?.equipe2.joueurs?.JB3);
        joueursX.push(match?.equipe2.joueurs?.JB4);
        setLiveTeamX(joueursX);
    }

    const mergeJsonPartieWithLivescore = (partieToMerge) => {
        let livescorePartie = livescoreRef.current.findIndex(partie => partie.id === partieToMerge.id);
        let tmpLivescore = [...livescoreRef.current];
        // on regarde si le match a été supprimé
        if (partieToMerge.status === "deleted") {
            // si on avait le match alors on supprime
            if (livescorePartie !== -1) {
                tmpLivescore = tmpLivescore.filter(partie => partie.id !== partieToMerge.id);
                livescoreRef.current = tmpLivescore;
                setLivescore(tmpLivescore)
            }
            return;
        }

        // on récupère le slotID des joueurs
        const slotIdJA = partieToMerge.joueur1.slotId;
        const slotIdJB = partieToMerge.joueur2.slotId;
        let fullNameJA = ""; let fullNameJB = "";

        if (slotIdJA !== "") {
            fullNameJA = jsonMatchRef.current.equipe1.joueurs[slotIdJA].nom + " " + jsonMatchRef.current.equipe1.joueurs[slotIdJA].prenom;
        }

        if (slotIdJB !== "") {
            fullNameJB = jsonMatchRef.current.equipe2.joueurs[slotIdJB].nom + " " + jsonMatchRef.current.equipe2.joueurs[slotIdJB].prenom;
        }

        const data = {
            "id": partieToMerge.id,
            "ja": fullNameJA,
            "jb": fullNameJB,
            "scorea": '' + CalSetA(partieToMerge.score),
            "scoreb": '' + CalSetX(partieToMerge.score),
            "detail": partieToMerge.score,
            "premier_serveur": partieToMerge.premier_serveur,
            "score_history": partieToMerge.score_history,
            "cards": partieToMerge.cards,
            "statut": partieToMerge.status,
            "countryCodeJA": jsonMatchRef.current.equipe1?.joueurs?.[slotIdJA]?.country_code,
            "countryCodeJB": jsonMatchRef.current.equipe2?.joueurs?.[slotIdJB]?.country_code,
        };

        // on update/insert
        if (livescorePartie !== -1) {
            tmpLivescore[livescorePartie] = data
        }
        else {
            tmpLivescore.push(data);
        }
        

        // on met à jour les matchs
        livescoreRef.current = tmpLivescore;
        setLivescore(tmpLivescore)
    }

    const isTerminate = () => {
        return headerLivescore.status === "termine" && details.length < 1;
    }

    const getTotalPartie = () => {
        let count = 0;
        if (headerLivescore.status === "en_cours" || isTerminate())
        {
            count = livescore.length;
        }

        details?.forEach((detail) => {
            if (detail?.scorea !== "-" || detail?.scoreb !== "-") {
                count++;
            }
        });
        return count;
    }

    if (getTotalPartie() === 0) return;

    return ( 
        <AnimateHeight 
            key={rencId} 
            id={rencId}
            duration={500}
            height={showDetails ? 'auto' : 0}
        >
            <div className='playoffdown_details_component'>

                {((isOnLive === true) &&(
                    <h1 style={{color: 'red'}}>En direct</h1>
                ))

                || (
                    <h1>{getTotalPartie()} partie(s) disputé(es)</h1>
                )}

                {/* Affichage du cartouche des joueurs */}
                {((headerLivescore.status === "en_cours" || isTerminate()) &&
                    <CartridgePlayers id={rencId} liveTeamA={liveTeamA} liveTeamX={liveTeamX} isFromSPID={false}></CartridgePlayers>)
                ||
                    <CartridgePlayers id={rencId} liveTeamA={liveTeamA} liveTeamX={liveTeamX} countryCodesA={liveTeamACountryCodes} countryCodesX={liveTeamXCountryCodes} isFromSPID={true}></CartridgePlayers>
                }
                {/* Affichage du détail des matchs */}
                <div style={{maxWidth: getWindowSize().innerWidth > 1000 ? 450 : 300}}>
                    {
                        ((headerLivescore.status === "en_cours" || isTerminate()) &&
                        livescore?.map((detail, index) => {
                            return (
                                (detail.scorea !== "-" || detail.scoreb !== "-") &&
                                <div
                                    key={index}
                                    style={{marginBottom: 20}}
                                >
                                    <Partie status={headerLivescore.status} match={detail} teamJA={homeClub} teamJB={awayClub} isLivescoring onRefreshHistorique={getDetailsDafunker} />
                                </div>
                            )
                        }))
                        ||
                        details?.map((detail, index) => {
                            return (
                                (detail.scorea !== "-" || detail.scoreb !== "-") &&
                                <div
                                    key={index} 
                                    style={{marginBottom: 20}}
                                >
                                    <Partie status={headerLivescore.status} match={detail} teamJA={homeClub} teamJB={awayClub} isOffDown onRefreshHistorique={refreshHistoriqueForDetails} />
                                </div>
                            )
                        })
                    }
                </div>
            </div>
        </AnimateHeight>
    );
}
 
export default PlayOffDownDetails;